import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, query, orderBy, getDoc, doc, addDoc, deleteDoc, updateDoc } from 'firebase/firestore';
import ConfirmationModal from '../components/ConfirmationModal';
import { toast } from 'react-toastify';

function Tutorial() {
  const { currentUser } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [sections, setSections] = useState([]);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewSectionModal, setShowNewSectionModal] = useState(false);
  const [showEditSectionModal, setShowEditSectionModal] = useState(false);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [editingSectionTitle, setEditingSectionTitle] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingSection, setDeletingSection] = useState(null);
  const [deletingTutorial, setDeletingTutorial] = useState(null);
  const [showNewTutorialModal, setShowNewTutorialModal] = useState(false);
  const [newTutorialData, setNewTutorialData] = useState({
    title: '',
    videoUrl: '',
    sectionId: null
  });
  const [showEditTutorialModal, setShowEditTutorialModal] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState({
    id: null,
    sectionId: null,
    title: '',
    videoUrl: ''
  });
  const [activeSection, setActiveSection] = useState(null);
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Add ref for the navigation section
  const navigationRef = React.useRef(null);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        setIsAdmin(userDoc.exists() && userDoc.data().isAdmin);
      }
    };

    const fetchTutorials = async () => {
      try {
        const sectionsQuery = query(collection(db, 'tutorialSections'), orderBy('order'));
        const sectionsSnapshot = await getDocs(sectionsQuery);
        
        const sectionsData = [];
        for (const sectionDoc of sectionsSnapshot.docs) {
          const section = { id: sectionDoc.id, ...sectionDoc.data() };
          
          // Fetch tutorials for this section
          const tutorialsQuery = query(
            collection(db, `tutorialSections/${section.id}/tutorials`),
            orderBy('order')
          );
          const tutorialsSnapshot = await getDocs(tutorialsQuery);
          
          section.tutorials = tutorialsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          sectionsData.push(section);
        }
        
        setSections(sectionsData);
      } catch (error) {
        console.error("Error fetching tutorials:", error);
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
    fetchTutorials();
  }, [currentUser]);

  const handleOpenVideo = (tutorial) => {
    setSelectedTutorial(tutorial);
  };

  const handleCloseVideo = () => {
    setSelectedTutorial(null);
  };

  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!newSectionTitle.trim()) return;

    try {
      const sectionsRef = collection(db, 'tutorialSections');
      const sectionsSnapshot = await getDocs(query(sectionsRef, orderBy('order', 'desc')));
      const lastOrder = sectionsSnapshot.empty ? 0 : sectionsSnapshot.docs[0].data().order;

      await addDoc(sectionsRef, {
        title: newSectionTitle.trim(),
        order: lastOrder + 1,
        createdAt: new Date()
      });

      setNewSectionTitle('');
      setShowNewSectionModal(false);
      toast.success('Section added successfully');
      
      // Refresh the sections
      const fetchTutorials = async () => {
        try {
          const sectionsQuery = query(collection(db, 'tutorialSections'), orderBy('order'));
          const sectionsSnapshot = await getDocs(sectionsQuery);
          
          const sectionsData = [];
          for (const sectionDoc of sectionsSnapshot.docs) {
            const section = { id: sectionDoc.id, ...sectionDoc.data() };
            
            // Fetch tutorials for this section
            const tutorialsQuery = query(
              collection(db, `tutorialSections/${section.id}/tutorials`),
              orderBy('order')
            );
            const tutorialsSnapshot = await getDocs(tutorialsQuery);
            
            section.tutorials = tutorialsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            sectionsData.push(section);
          }
          
          setSections(sectionsData);
        } catch (error) {
          console.error("Error fetching tutorials:", error);
        }
      };
      fetchTutorials();
    } catch (error) {
      console.error("Error adding section:", error);
      toast.error('Failed to add section');
    }
  };

  const handleEditSection = async (e) => {
    e.preventDefault();
    if (!editingSectionTitle.trim() || !editingSectionId) return;

    try {
      const sectionRef = doc(db, 'tutorialSections', editingSectionId);
      await updateDoc(sectionRef, {
        title: editingSectionTitle.trim(),
        updatedAt: new Date()
      });

      setEditingSectionTitle('');
      setEditingSectionId(null);
      setShowEditSectionModal(false);
      toast.success('Section updated successfully');
      
      // Refresh the sections
      const fetchTutorials = async () => {
        try {
          const sectionsQuery = query(collection(db, 'tutorialSections'), orderBy('order'));
          const sectionsSnapshot = await getDocs(sectionsQuery);
          
          const sectionsData = [];
          for (const sectionDoc of sectionsSnapshot.docs) {
            const section = { id: sectionDoc.id, ...sectionDoc.data() };
            
            // Fetch tutorials for this section
            const tutorialsQuery = query(
              collection(db, `tutorialSections/${section.id}/tutorials`),
              orderBy('order')
            );
            const tutorialsSnapshot = await getDocs(tutorialsQuery);
            
            section.tutorials = tutorialsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            sectionsData.push(section);
          }
          
          setSections(sectionsData);
        } catch (error) {
          console.error("Error fetching tutorials:", error);
        }
      };
      fetchTutorials();
    } catch (error) {
      console.error("Error updating section:", error);
      toast.error('Failed to update section');
    }
  };

  const handleDeleteSection = async () => {
    if (!deletingSection) return;

    try {
      await deleteDoc(doc(db, 'tutorialSections', deletingSection.id));
      setShowDeleteModal(false);
      setDeletingSection(null);
      toast.success('Section deleted successfully');
      
      // Refresh the sections
      const fetchTutorials = async () => {
        try {
          const sectionsQuery = query(collection(db, 'tutorialSections'), orderBy('order'));
          const sectionsSnapshot = await getDocs(sectionsQuery);
          
          const sectionsData = [];
          for (const sectionDoc of sectionsSnapshot.docs) {
            const section = { id: sectionDoc.id, ...sectionDoc.data() };
            
            // Fetch tutorials for this section
            const tutorialsQuery = query(
              collection(db, `tutorialSections/${section.id}/tutorials`),
              orderBy('order')
            );
            const tutorialsSnapshot = await getDocs(tutorialsQuery);
            
            section.tutorials = tutorialsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            sectionsData.push(section);
          }
          
          setSections(sectionsData);
        } catch (error) {
          console.error("Error fetching tutorials:", error);
        }
      };
      fetchTutorials();
    } catch (error) {
      console.error("Error deleting section:", error);
      toast.error('Failed to delete section');
    }
  };

  const handleDeleteTutorial = async () => {
    if (!deletingTutorial || !deletingSection) return;

    try {
      await deleteDoc(doc(db, `tutorialSections/${deletingSection.id}/tutorials`, deletingTutorial.id));
      setShowDeleteModal(false);
      setDeletingTutorial(null);
      setDeletingSection(null);
      toast.success('Tutorial deleted successfully');
      
      // Refresh the sections
      const fetchTutorials = async () => {
        try {
          const sectionsQuery = query(collection(db, 'tutorialSections'), orderBy('order'));
          const sectionsSnapshot = await getDocs(sectionsQuery);
          
          const sectionsData = [];
          for (const sectionDoc of sectionsSnapshot.docs) {
            const section = { id: sectionDoc.id, ...sectionDoc.data() };
            
            // Fetch tutorials for this section
            const tutorialsQuery = query(
              collection(db, `tutorialSections/${section.id}/tutorials`),
              orderBy('order')
            );
            const tutorialsSnapshot = await getDocs(tutorialsQuery);
            
            section.tutorials = tutorialsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            sectionsData.push(section);
          }
          
          setSections(sectionsData);
        } catch (error) {
          console.error("Error fetching tutorials:", error);
        }
      };
      fetchTutorials();
    } catch (error) {
      console.error("Error deleting tutorial:", error);
      toast.error('Failed to delete tutorial');
    }
  };

  const openEditSectionModal = (section) => {
    setEditingSectionId(section.id);
    setEditingSectionTitle(section.title);
    setShowEditSectionModal(true);
  };

  const openDeleteModal = (section, tutorial = null) => {
    setDeletingSection(section);
    setDeletingTutorial(tutorial);
    setShowDeleteModal(true);
  };

  const handleAddTutorial = async (e) => {
    e.preventDefault();
    if (!newTutorialData.title.trim() || !newTutorialData.videoUrl.trim() || !newTutorialData.sectionId) return;

    try {
      const tutorialsRef = collection(db, `tutorialSections/${newTutorialData.sectionId}/tutorials`);
      const tutorialsSnapshot = await getDocs(query(tutorialsRef, orderBy('order', 'desc')));
      const lastOrder = tutorialsSnapshot.empty ? 0 : tutorialsSnapshot.docs[0].data().order;

      // Extract video ID from YouTube URL
      const videoId = newTutorialData.videoUrl.includes('youtube.com') 
        ? newTutorialData.videoUrl.split('v=')[1]?.split('&')[0]
        : newTutorialData.videoUrl.includes('youtu.be')
          ? newTutorialData.videoUrl.split('youtu.be/')[1]?.split('?')[0]
          : newTutorialData.videoUrl;

      await addDoc(tutorialsRef, {
        title: newTutorialData.title.trim(),
        videoUrl: videoId,
        order: lastOrder + 1,
        createdAt: new Date()
      });

      setNewTutorialData({
        title: '',
        videoUrl: '',
        sectionId: null
      });
      setShowNewTutorialModal(false);
      toast.success('Tutorial added successfully');
      
      // Refresh the sections
      const fetchTutorials = async () => {
        try {
          const sectionsQuery = query(collection(db, 'tutorialSections'), orderBy('order'));
          const sectionsSnapshot = await getDocs(sectionsQuery);
          
          const sectionsData = [];
          for (const sectionDoc of sectionsSnapshot.docs) {
            const section = { id: sectionDoc.id, ...sectionDoc.data() };
            
            // Fetch tutorials for this section
            const tutorialsQuery = query(
              collection(db, `tutorialSections/${section.id}/tutorials`),
              orderBy('order')
            );
            const tutorialsSnapshot = await getDocs(tutorialsQuery);
            
            section.tutorials = tutorialsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            sectionsData.push(section);
          }
          
          setSections(sectionsData);
        } catch (error) {
          console.error("Error fetching tutorials:", error);
        }
      };
      fetchTutorials();
    } catch (error) {
      console.error("Error adding tutorial:", error);
      toast.error('Failed to add tutorial');
    }
  };

  const handleEditTutorial = async (e) => {
    e.preventDefault();
    if (!editingTutorial.title.trim() || !editingTutorial.videoUrl.trim()) return;

    try {
      const tutorialRef = doc(db, `tutorialSections/${editingTutorial.sectionId}/tutorials`, editingTutorial.id);
      
      // Extract video ID from YouTube URL
      const videoId = editingTutorial.videoUrl.includes('youtube.com') 
        ? editingTutorial.videoUrl.split('v=')[1]?.split('&')[0]
        : editingTutorial.videoUrl.includes('youtu.be')
          ? editingTutorial.videoUrl.split('youtu.be/')[1]?.split('?')[0]
          : editingTutorial.videoUrl;

      await updateDoc(tutorialRef, {
        title: editingTutorial.title.trim(),
        videoUrl: videoId,
        updatedAt: new Date()
      });

      setEditingTutorial({
        id: null,
        sectionId: null,
        title: '',
        videoUrl: ''
      });
      setShowEditTutorialModal(false);
      toast.success('Tutorial updated successfully');
      
      // Refresh the sections
      const fetchTutorials = async () => {
        try {
          const sectionsQuery = query(collection(db, 'tutorialSections'), orderBy('order'));
          const sectionsSnapshot = await getDocs(sectionsQuery);
          
          const sectionsData = [];
          for (const sectionDoc of sectionsSnapshot.docs) {
            const section = { id: sectionDoc.id, ...sectionDoc.data() };
            
            // Fetch tutorials for this section
            const tutorialsQuery = query(
              collection(db, `tutorialSections/${section.id}/tutorials`),
              orderBy('order')
            );
            const tutorialsSnapshot = await getDocs(tutorialsQuery);
            
            section.tutorials = tutorialsSnapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            sectionsData.push(section);
          }
          
          setSections(sectionsData);
        } catch (error) {
          console.error("Error fetching tutorials:", error);
        }
      };
      fetchTutorials();
    } catch (error) {
      console.error("Error updating tutorial:", error);
      toast.error('Failed to update tutorial');
    }
  };

  const openEditTutorialModal = (section, tutorial) => {
    setEditingTutorial({
      id: tutorial.id,
      sectionId: section.id,
      title: tutorial.title,
      videoUrl: tutorial.videoUrl
    });
    setShowEditTutorialModal(true);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      const yOffset = -100; 
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      
      window.scrollTo({
        top: y,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    setActiveSection(null);
  };

  // Update scroll event listener to also handle active section highlighting
  useEffect(() => {
    const handleScroll = () => {
      // Back to top button visibility
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
      
      // Clear active section when scrolled back to navigation area
      if (navigationRef.current) {
        const navRect = navigationRef.current.getBoundingClientRect();
        // If navigation is visible in the viewport (with some buffer)
        if (navRect.top > -50 && navRect.bottom > 0) {
          setActiveSection(null);
        } else {
          // Check which section is currently in view
          sections.forEach(section => {
            const element = document.getElementById(`section-${section.id}`);
            if (element) {
              const rect = element.getBoundingClientRect();
              // If section is visible in the viewport
              if (rect.top < window.innerHeight/2 && rect.bottom > 0) {
                setActiveSection(section.id);
              }
            }
          });
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections]); // Add sections as dependency

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-white">Loading tutorials...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Tutorial Videos
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Discover how to make the most of Cashsule with step-by-step video tutorials.
          </p>
        </motion.div>

        {/* Quick Navigation Section - No longer sticky */}
        {sections.length > 0 && (
          <motion.div
            ref={navigationRef}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md"
          >
            <h2 className="text-lg font-semibold mb-3 text-gray-800 dark:text-white">Quick Navigation</h2>
            <div className="flex flex-wrap gap-2">
              {sections.map((section) => (
                <button
                  key={`nav-${section.id}`}
                  onClick={() => scrollToSection(section.id)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-all duration-300 ${
                    activeSection === section.id
                      ? 'bg-indigo-500 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                  }`}
                >
                  {section.title}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Admin Controls */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <button 
              onClick={() => setShowNewSectionModal(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Add New Section
            </button>
          </motion.div>
        )}

        {/* Sections Grid */}
        <div className="space-y-6 sm:space-y-8">
          {sections.map((section) => (
            <motion.div
              key={section.id}
              id={`section-${section.id}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-8 shadow-lg scroll-mt-32"
            >
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-0">{section.title}</h3>
                {isAdmin && (
                  <div className="flex space-x-3">
                    <button 
                      onClick={() => openEditSectionModal(section)}
                      className="flex-1 sm:flex-none bg-blue-100 hover:bg-blue-200 dark:bg-blue-500/20 dark:hover:bg-blue-500/30 text-blue-600 dark:text-blue-300 px-4 py-2 rounded-lg transition-all duration-300 text-sm sm:text-base"
                    >
                      Edit Section
                    </button>
                    <button 
                      onClick={() => openDeleteModal(section)}
                      className="flex-1 sm:flex-none bg-red-100 hover:bg-red-200 dark:bg-red-500/20 dark:hover:bg-red-500/30 text-red-600 dark:text-red-300 px-4 py-2 rounded-lg transition-all duration-300 text-sm sm:text-base"
                    >
                      Delete Section
                    </button>
                  </div>
                )}
              </div>

              {/* Tutorials List */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {section.tutorials.map((tutorial) => (
                  <motion.div
                    key={tutorial.id}
                    whileHover={{ scale: 1.02 }}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 sm:p-4 cursor-pointer hover:shadow-lg transition-all duration-300"
                    onClick={() => handleOpenVideo(tutorial)}
                  >
                    <div className="space-y-2 sm:space-y-3">
                      <div className="aspect-video bg-black/30 rounded-lg overflow-hidden">
                        <img
                          src={`https://img.youtube.com/vi/${tutorial.videoUrl}/maxresdefault.jpg`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://img.youtube.com/vi/${tutorial.videoUrl}/hqdefault.jpg`;
                          }}
                          alt={tutorial.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-sm sm:text-base text-gray-900 dark:text-white font-medium">{tutorial.title}</h4>
                        {isAdmin && (
                          <div className="flex space-x-2 pt-2">
                            <button 
                              className="flex-1 text-xs sm:text-sm text-blue-400 hover:text-blue-300 bg-blue-500/10 rounded-md py-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditTutorialModal(section, tutorial);
                              }}
                            >
                              Edit
                            </button>
                            <button 
                              className="flex-1 text-xs sm:text-sm text-red-400 hover:text-red-300 bg-red-500/10 rounded-md py-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteModal(section, tutorial);
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
                {isAdmin && (
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    className="bg-white/5 rounded-lg p-4 h-full min-h-[200px] flex items-center justify-center hover:bg-white/10 transition-all duration-300"
                    onClick={() => {
                      setNewTutorialData(prev => ({ ...prev, sectionId: section.id }));
                      setShowNewTutorialModal(true);
                    }}
                  >
                    <div className="text-gray-600 opacity-60 hover:opacity-100 flex flex-col items-center space-y-2">
                      <span className="text-3xl">+</span>
                      <span>Add Tutorial</span>
                    </div>
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Back to Top Button */}
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.5 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 bg-indigo-500 hover:bg-indigo-600 text-white p-3 rounded-full shadow-lg z-50 transition-all duration-300"
            aria-label="Back to top"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
          </motion.button>
        )}
        {/* Video Modal */}
        {selectedTutorial && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-900 p-3 sm:p-4 rounded-xl w-full max-w-3xl">
              <div className="flex justify-between items-center mb-3 sm:mb-4">
                <h3 className="text-lg sm:text-xl font-semibold text-white pr-4">{selectedTutorial.title}</h3>
                <button
                  onClick={handleCloseVideo}
                  className="text-white hover:text-gray-300 p-1"
                >
                  ✕
                </button>
              </div>
              <div className="relative pt-[56.25%]">
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-lg"
                  src={`https://www.youtube.com/embed/${selectedTutorial.videoUrl}`}
                  title={selectedTutorial.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        )}

        {/* New Section Modal */}
        {showNewSectionModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900 p-6 rounded-xl w-full max-w-md">
              <h3 className="text-xl font-semibold text-white mb-4">Add New Section</h3>
              <form onSubmit={handleAddSection}>
                <input
                  type="text"
                  value={newSectionTitle}
                  onChange={(e) => setNewSectionTitle(e.target.value)}
                  placeholder="Section Title"
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-2 mb-4"
                  autoFocus
                />
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowNewSectionModal(false)}
                    className="px-4 py-2 text-gray-400 hover:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
                  >
                    Create Section
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Section Modal */}
        {showEditSectionModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900 p-6 rounded-xl w-full max-w-md">
              <h3 className="text-xl font-semibold text-white mb-4">Edit Section</h3>
              <form onSubmit={handleEditSection}>
                <input
                  type="text"
                  value={editingSectionTitle}
                  onChange={(e) => setEditingSectionTitle(e.target.value)}
                  placeholder="Section Title"
                  className="w-full bg-gray-800 text-white rounded-lg px-4 py-2 mb-4"
                  autoFocus
                />
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowEditSectionModal(false)}
                    className="px-4 py-2 text-gray-400 hover:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeletingSection(null);
            setDeletingTutorial(null);
          }}
          onConfirm={deletingTutorial ? handleDeleteTutorial : handleDeleteSection}
          title={deletingTutorial ? "Delete Tutorial" : "Delete Section"}
          message={deletingTutorial 
            ? "Are you sure you want to delete this tutorial? This action cannot be undone."
            : "Are you sure you want to delete this section? This will also delete all tutorials within this section. This action cannot be undone."
          }
        />

        {/* New Tutorial Modal */}
        {showNewTutorialModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900 p-6 rounded-xl w-full max-w-md">
              <h3 className="text-xl font-semibold text-white mb-4">Add New Tutorial</h3>
              <form onSubmit={handleAddTutorial}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={newTutorialData.title}
                      onChange={(e) => setNewTutorialData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Tutorial Title"
                      className="w-full bg-gray-800 text-white rounded-lg px-4 py-2"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      YouTube Video URL or ID
                    </label>
                    <input
                      type="text"
                      value={newTutorialData.videoUrl}
                      onChange={(e) => setNewTutorialData(prev => ({ ...prev, videoUrl: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=... or video ID"
                      className="w-full bg-gray-800 text-white rounded-lg px-4 py-2"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewTutorialModal(false);
                      setNewTutorialData({ title: '', videoUrl: '', sectionId: null });
                    }}
                    className="px-4 py-2 text-gray-400 hover:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
                  >
                    Add Tutorial
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Tutorial Modal */}
        {showEditTutorialModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
            <div className="bg-gray-900 p-6 rounded-xl w-full max-w-md">
              <h3 className="text-xl font-semibold text-white mb-4">Edit Tutorial</h3>
              <form onSubmit={handleEditTutorial}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Title
                    </label>
                    <input
                      type="text"
                      value={editingTutorial.title}
                      onChange={(e) => setEditingTutorial(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Tutorial Title"
                      className="w-full bg-gray-800 text-white rounded-lg px-4 py-2"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      YouTube Video URL or ID
                    </label>
                    <input
                      type="text"
                      value={editingTutorial.videoUrl}
                      onChange={(e) => setEditingTutorial(prev => ({ ...prev, videoUrl: e.target.value }))}
                      placeholder="https://youtube.com/watch?v=... or video ID"
                      className="w-full bg-gray-800 text-white rounded-lg px-4 py-2"
                    />
                  </div>
                  {editingTutorial.videoUrl && (
                    <div className="aspect-video bg-black/30 rounded-lg overflow-hidden">
                      <img
                        src={`https://img.youtube.com/vi/${editingTutorial.videoUrl}/maxresdefault.jpg`}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = `https://img.youtube.com/vi/${editingTutorial.videoUrl}/hqdefault.jpg`;
                        }}
                        alt="Video thumbnail preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditTutorialModal(false);
                      setEditingTutorial({
                        id: null,
                        sectionId: null,
                        title: '',
                        videoUrl: ''
                      });
                    }}
                    className="px-4 py-2 text-gray-400 hover:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Tutorial;
